import * as React from 'react';
import Typography from '@mui/material/Typography';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ForwardIcon from '@mui/icons-material/Forward';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { ImageComment } from '../../imagePreview/queries/use-query-edited-image';
import { CommentRecipient, getCommentRecipients, getRecipientTitleKey } from '../state/utils';
import { CommentTimestamp } from './CommentTimestamp';

const CommentPanel = styled('article')({
    backgroundColor: '#eceff0',
    border: '1px solid #c5cdcd',
    borderRadius: '2px'
});

const CommentHeader = styled('header')({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: '#cfd7d7',
    borderBottom: '1px solid #c5cdcd',
    fontSize: '14px',
    color: '#222'
});

const Author = styled('span')({
    fontWeight: 600
});

const Spacer = styled('span')({
    flex: 1
});

const CommentBody = styled('p')({
    margin: 0,
    padding: '12px 16px',
    fontSize: '14px',
    whiteSpace: 'pre-wrap'
});

const RECIPIENT_ICONS = {
    [CommentRecipient.Photographer]: CameraAltIcon,
    [CommentRecipient.ImageEditor]: ColorLensIcon,
    [CommentRecipient.Se360]: HeadsetMicIcon
};

type ImageCommentListProps = {
    comments: ImageComment[];
};

export function ImageCommentList({ comments }: ImageCommentListProps) {
    return (
        <>
            {comments.map((comment) => (
                <CommentPanel key={comment.id} data-testid="image-comment">
                    <CommentHeader>
                        <Author>{comment.author}</Author>
                        <ForwardIcon sx={{ fontSize: 18 }} aria-hidden="true" />
                        {getCommentRecipients(comment).map((recipient) => {
                            let Icon = RECIPIENT_ICONS[recipient];
                            let title = Translate(getRecipientTitleKey(recipient));
                            return (
                                <Icon
                                    key={recipient}
                                    role="img"
                                    aria-label={title}
                                    titleAccess={title}
                                    sx={{ fontSize: 18 }}
                                />
                            );
                        })}
                        <Spacer />
                        <CommentTimestamp time={comment.time} />
                    </CommentHeader>
                    <CommentBody>
                        <Typography component="span" sx={{ fontSize: '14px' }}>
                            {comment.text}
                        </Typography>
                    </CommentBody>
                </CommentPanel>
            ))}
        </>
    );
}
